import { useApplicationModals } from "app/common/hooks";
import MainLayout from "app/layouts/main";
import { Formik, getIn, useFormikContext } from "formik";
import * as Yup from "yup";
import { InputLabel } from "app/components/atoms/form";
import { useUpdateForm } from "app/integration/common/hooks";
import api from "app/integration/api";
import { useCollector } from "app/integration/common/hooks";
import { FormHeader, FormSummary } from "app/components/atoms/layout";
import { useCallback, useEffect } from "react";
import Table from "app/components/atoms/table";
import { Button, IconButton } from "app/components/atoms/button";
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AppModals from "app/modals";
import clsx from "clsx";
import style from "style/height.module.css";
import { ChevronDownIcon, ChevronUpIcon, PlusCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import React from "react";
import { ErrorMessage } from "app/components/atoms/form";
import { InputMolecule } from "app/components/molecules/form";
import { useToast } from "app/components/atoms/toast/toast";
import { clampReturQty, isPerQtyOrderItem, sisaRetur } from "app/common/returCustomer";
import { useBarcodeScanner } from "app/context/BarcodeScannerContext";


const breadcrumbItems = [
    { label: "Retur Customer", href: "/retur-customer" },
    { label: "Tambah Retur Customer", href: "#" },
];

function CreateReturCustomer() {
    let isError = false;
    const navigate = useNavigate();

    const { openModal, setPayloads, closeModal } =
        useApplicationModals();

    const handleSuccess = () => {
        navigate("/retur-customer");
    };
    const formikRef = useRef(null);
    const { showToast } = useToast();
    const [expandedRows, setExpandedRows] = useState({});
    const toggleRow = (key) => {
        setExpandedRows(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const [productVariants] = useCollector({ modelName: "productVariant" });
    const [qrCode, setQrCode] = useState("");
    const scanBarcodeInputRef = useRef(null);
    const scanQueueRef = useRef(Promise.resolve());
    const [order, setOrder] = useState(null);
    const orderRef = useRef(order);
    useEffect(() => {
        orderRef.current = order;
    }, [order]);
    // Jumlah yang SUDAH diretur per varian pada order ini, dipakai sebagai batas
    // atas baris per qty. Produk ber-barcode unik tidak memerlukannya: batasnya
    // sudah dijaga oleh ketersediaan `product_codes` berstatus `delivery`.
    const [returnedQty, setReturnedQty] = useState({});
    // Riwayat retur gagal dimuat (bukan 404): batas atas tidak diketahui, jadi
    // penambahan baris per qty ditahan daripada memakai batas yang salah.
    const [gagalRiwayatRetur, setGagalRiwayatRetur] = useState(false);

    useEffect(() => {
        const salesOrderId = order?.sales_order_id;
        if (!salesOrderId) {
            setReturnedQty({});
            setGagalRiwayatRetur(false);
            return;
        }
        let batal = false;
        (async () => {
            try {
                const response = await api.returCustomer.index({
                    transaction_id: salesOrderId,
                    limit: 1000,
                });
                const total = {};
                for (const row of response?.data ?? []) {
                    const variantId = row?.product_variant_id;
                    total[variantId] = (total[variantId] ?? 0) + Number(row?.qty ?? 0);
                }
                if (!batal) {
                    setReturnedQty(total);
                    setGagalRiwayatRetur(false);
                }
            } catch (err) {
                if (batal) return;
                // HANYA 404 yang berarti "belum ada retur" — itu jawaban backend
                // untuk daftar kosong. Kegagalan lain (401/403/5xx/jaringan)
                // TIDAK boleh dibaca sebagai nol: batas atas retur ikut jadi nol
                // dan kasir bisa meretur lebih banyak dari yang dibeli tanpa ada
                // yang menahan. Karena itu ditandai gagal dan jalur per qty
                // dikunci sampai datanya benar-benar terbaca.
                setReturnedQty({});
                setGagalRiwayatRetur(err?.response?.status !== 404);
            }
        })();
        return () => { batal = true; };
    }, [order?.sales_order_id]);

    /** Sisa yang masih boleh diretur untuk satu baris order per qty. */
    const sisaReturPerQty = useCallback(
        (orderItem) => sisaRetur(orderItem?.qty, returnedQty[orderItem?.product_variant?.id]),
        [returnedQty]
    );

    const { initialValues, setInitialValues, validationSchema, onSubmit } = useUpdateForm({
        initialValues: {
            notes: "",
            items: [],
        },
        validationSchema: Yup.object().shape({
            items: Yup.array().of(Yup.object().shape({
                product_variant_id: Yup.number().required("Variant harus diisi"),
                product_code: Yup.array().of(Yup.object().shape({
                    qr_code: Yup.string().required("Barcode harus diisi"),
                })),
                // Baris per qty bisa diketik jumlahnya, jadi batas bawahnya
                // dijaga di sini; batas atas dijaga saat pengetikan (sisa retur).
                qty: Yup.number().required("Jumlah harus diisi").min(1, "Jumlah minimal 1"),
            })).required("Item harus diisi. Silahkan tambahkan item melalui input barcode.").min(1, "Item harus diisi"),
        }),
        onSubmit: async (values) => {
            try {
                for (let index = 0; index < values?.items?.length; index++) {
                    const item = values?.items[index];

                    const returCustomer = await api.returCustomer.store(
                        order?.sales_order_id,
                        order?.customer_id,
                        item?.product_variant?.product_detail?.name,
                        item?.product_variant_id,
                        item?.qty,
                        values?.notes,
                        "approved"
                    );

                    for (let i = 0; i < item?.product_code?.length; i++) {
                        await api.productCode.update(item?.product_code[i]?.id, {
                            retur_customer_id: returCustomer?.data?.id,
                            status: "warehouse",
                            scan_retur_date: new Date(),
                            retur_reason: item?.product_code[i]?.notes,
                        });
                    }
                }
                isError = false;
            } catch (error) {
                setPayloads("errorMessage.view", {
                    title: "Add Retur Supplier Error",
                    message: error?.response?.data?.message,
                });
                isError = true;
                openModal("errorMessage.view");
            }
        },
        onSuccess: () => {
            if (!isError) {
                handleSuccess();
            }
        }
    });

    /**
     * Menjalankan pencarian daftar dan memperlakukan 404 sebagai "tidak ada",
     * bukan galat. Endpoint CRUD backend menjawab 404 + "Belum ada data" untuk
     * hasil kosong (`CrudController.getAll`), jadi tanpa ini setiap pencarian
     * yang nihil menjadi modal galat.
     */
    const cariDaftar = useCallback(async (panggil) => {
        try {
            return await panggil();
        } catch (err) {
            if (err?.response?.status === 404) return { data: [] };
            throw err;
        }
    }, []);

    /**
     * Mencari varian lewat `product_variants.qr_code` — barcode yang dipakai
     * bersama oleh seluruh unit sebuah varian. Dipakai HANYA sebagai cadangan
     * saat pencarian `product_codes` gagal, untuk membedakan "produk per qty"
     * dari "barcode salah". Mengembalikan null kalau tidak ada (backend
     * menjawab 404 untuk daftar kosong).
     */
    const cariVarianDariBarcode = useCallback(async (value) => {
        try {
            const response = await cariDaftar(() => api.productVariant.index({
                qr_code: value?.replace(/"/g, ""),
                limit: 1,
            }));
            return response?.data?.[0] ?? null;
        } catch (err) {
            return null;
        }
    }, [cariDaftar]);

    /**
     * Mengambil data unit produk (product code) berdasarkan nilai barcode yang dipindai,
     * dan memastikan unit tersebut termasuk dalam OrderItems dari pesanan yang aktif.
     *
     * @param {string} value - Nilai barcode/QR code yang dipindai.
     * @returns {Promise<object|null>} Objek product code beserta variannya jika valid, atau null jika gagal.
     * @sideEffects
     * - Membuka modal errorMessage.view jika unit tidak ditemukan atau terjadi error API.
     */
    const setItemOrder = useCallback(async (value) => {
        try {
            // Get product code directly from backend with specific QR code
            //
            // Daftar KOSONG dijawab 404 ("Belum ada data") oleh backend, bukan
            // 200 dengan array kosong. Tanpa `cariDaftar`, barcode yang tidak
            // cocok dengan satu pun unit langsung terlempar ke catch di bawah
            // dan kasir hanya melihat "Belum ada data" — jalur cadangan varian
            // di bawah tidak pernah tercapai.
            const response = await cariDaftar(() => api.productCode.index({
                qr_code: value?.replace(/"/g, ""),  // Remove all quotes globally
                status: "delivery",
                limit: 1
            }));

            let productCode = response?.data?.[0];
            const currentOrder = orderRef.current || order;
            if (currentOrder) {
                productCode = response?.data?.filter((element) => {
                    return currentOrder?.OrderItems?.find((item => item?.product_variant?.id == element?.product_variant_id))
                })[0];
            }

            // Check if product code exists and is in warehouse status
            if (!productCode) {
                // Barcode tidak cocok dengan satu pun unit. Sebelum menyerah,
                // dicoba sebagai barcode VARIAN: produk per qty memakai satu
                // barcode untuk semua unitnya dan tidak punya `product_codes`
                // sama sekali, jadi tanpa cabang ini kasir hanya melihat
                // "product code not found" dan tidak tahu harus lewat mana.
                const variant = await cariVarianDariBarcode(value);
                if (variant) {
                    const perQty = variant?.product_detail?.is_unique_barcode === false;
                    setPayloads("errorMessage.view", {
                        title: perQty ? "Produk dilacak per qty" : "Unit tidak ditemukan",
                        message: perQty
                            ? `${variant?.product_detail?.name ?? "Produk ini"} dilacak per qty (tanpa barcode unik) sehingga tidak bisa discan. Tambahkan lewat tombol Pilih Order, lalu isi jumlah returnya.`
                            : `Unit dengan barcode ${value} tidak berstatus terkirim — mungkin sudah diretur atau belum dikirim.`,
                    });
                    isError = true;
                    openModal("errorMessage.view");
                    return null;
                }

                setPayloads("errorMessage.view", {
                    title: "Scan Error",
                    message: `Product code not found for QR code: ${value}`,
                });
                isError = true;
                openModal("errorMessage.view");
                return null;
            }

            const responseVariant = await api.productVariant.index({
                id: productCode?.product_variant_id,
                limit: 1
            });

            const productVariant = responseVariant.data[0];
            // Check if product variant exists
            if (!productVariant) {
                console.warn(`Product variant not found for QR code: ${value}`);

                setPayloads("errorMessage.view", {
                    title: "Tambah Customer Error",
                    message: `Product variant not found for QR code: ${value}`,
                });
                isError = true;
                openModal("errorMessage.view");
                return null;
            }
            productCode.product_variant = productVariant;
            return productCode;
        } catch (err) {
            console.error("Error fetching product code:", err);
            setPayloads("errorMessage.view", {
                title: "API Error",
                message: err?.response?.data?.message || "Error fetching product code",
            });
            isError = true;
            openModal("errorMessage.view");
            return null;
        }
    }, [productVariants, setPayloads, openModal, cariVarianDariBarcode, cariDaftar, order]);  // Add dependencies array

    /**
     * Memproses string barcode (dari scanner hardware global maupun input teks manual),
     * mencari data unit pesanan terkait, dan menambahkannya ke state Formik items.
     *
     * @param {string} barcodeValue - String barcode yang dipindai atau diketik.
     * @param {object} currentValues - Objek values Formik saat ini.
     * @param {Function} setFieldValueFn - Fungsi setFieldValue Formik.
     * @returns {Promise<void>}
     * @sideEffects
     * - Memperbarui state order dan Formik items.
     * - Memperbarui state expandedRows untuk baris item yang baru ditambahkan.
     */
    const processScannedBarcode = async (barcodeValue, currentValues, setFieldValueFn) => {
        const qrValue = barcodeValue?.trim();
        if (!qrValue) return;

        try {
            const itemOrder = await setItemOrder(qrValue);
            if (!itemOrder) return;

            const isAlreadyScanned = (currentValues?.items || []).some((item) =>
                item.product_code?.some((code) => code?.qr_code === itemOrder?.qr_code)
            );
            if (isAlreadyScanned) {
                showToast(`Barcode "${qrValue}" sudah ada di dalam daftar!`, "warning");
                setQrCode("");
                return;
            }

            const salesOrder = await api.salesOrder.index({
                    id: itemOrder?.sales_order_id,
                    limit: 1,
                });

                const updatedOrderItems = salesOrder?.data?.[0]?.OrderItems?.map((item) => {
                    const product_variant = productVariants?.find(
                        (variant) =>
                            variant?.product_id === item?.product_id &&
                            variant?.color_id === item?.color_id &&
                            variant?.size_id === item?.size_id
                    );

                    return {
                        ...item, // Spread the existing properties
                        product_variant, // Add the new property
                    };
                });

                // Create a new data object with updated OrderItems
                const updatedData = {
                    MCustomer: salesOrder?.data?.[0]?.MCustomer,
                    SalesOrder: salesOrder?.data?.[0],
                    OrderItems: updatedOrderItems,
                    sales_order_id: salesOrder?.data?.[0]?.id,
                    customer_id: salesOrder?.data?.[0]?.customer_id,
                };

                const salesOrderId = salesOrder?.data?.[0]?.id;
                const currentActiveOrder = orderRef.current || order;
                const isSameOrder = !currentActiveOrder?.SalesOrder?.id || salesOrderId == currentActiveOrder?.SalesOrder?.id;

                setOrder(updatedData);
                orderRef.current = updatedData;

                if (isSameOrder) {
                    const existingIndex = (currentValues?.items || []).findIndex(
                        (item) => item.product_variant_id === itemOrder.product_variant_id
                    );
                    if (existingIndex > -1) {
                        const updatedItems = currentValues.items.map((item, idx) => {
                            if (idx === existingIndex) {
                                return {
                                    ...item,
                                    product_code: [...(item.product_code || []), itemOrder],
                                    qty: (Number(item.qty) || 0) + 1,
                                };
                            }
                            return item;
                        });
                        setFieldValueFn("items", updatedItems);
                        setExpandedRows((prev) => ({ ...prev, [existingIndex]: true }));
                    } else {
                        const newIndex = (currentValues?.items || []).length;
                        const newVariant = {
                            product_variant_id: itemOrder.product_variant_id,
                            product_variant: itemOrder.product_variant,
                            product_code: [itemOrder],
                            qty: 1,
                        };
                        setFieldValueFn("items", [...(currentValues?.items || []), newVariant]);
                        setExpandedRows((prev) => ({ ...prev, [newIndex]: true }));
                    }
                } else {
                    const newVariant = {
                        product_variant_id: itemOrder.product_variant_id,
                        product_variant: itemOrder.product_variant,
                        product_code: [itemOrder],
                        qty: 1,
                    };
                    setFieldValueFn("items", [newVariant]);
                    setExpandedRows({ 0: true });
                }
                setQrCode("");
        } catch (err) {
            console.error("[ReturCustomer Create] Error processing barcode:", err);
            showToast(`Gagal memproses barcode "${qrValue}"`, "error");
        }
    };

    /**
     * Listener global pemindai barcode untuk halaman Tambah Retur Customer.
     * Menggunakan scanQueueRef untuk mengeksekusi pemindaian secara serial dan mencegah race condition.
     *
     * @param {string} scannedBarcode - Nilai barcode yang telah dibersihkan oleh scanner buffer global.
     * @returns {void}
     * @sideEffects Menjadwalkan pemrosesan barcode ke antrian serial scanQueueRef.
     */
    const handleGlobalBarcodeScan = (scannedBarcode) => {
        if (!scannedBarcode || !formikRef.current) return;
        scanQueueRef.current = scanQueueRef.current
            .then(async () => {
                if (!formikRef.current) return;
                const { values, setFieldValue } = formikRef.current;
                await processScannedBarcode(scannedBarcode, values, setFieldValue);
            })
            .catch((err) => {
                console.error("[ReturCustomer Create] Scan queue error:", err);
            });
    };

    useBarcodeScanner(handleGlobalBarcodeScan, { info: "Retur Customer" });

    /**
     * Menangani event pengetikan dan penekanan Enter pada input barcode manual.
     * Mengalirkan pemindaian ke antrian serial yang sama untuk konsistensi state.
     *
     * @param {React.KeyboardEvent|React.ChangeEvent} e - Event input atau keyboard.
     * @param {object} formikProps - Objek bantuan Formik { setFieldValue, values }.
     * @returns {void}
     */
    const handleScanBarcodeChange = (e, { setFieldValue, values }) => {
        // Immediately refocus the input after any change
        if (scanBarcodeInputRef.current) {
            scanBarcodeInputRef.current.focus();
        }

        // Only process when Enter key is pressed
        if (e.key === "Enter" && e.target.value?.trim() !== "") {
            const val = e.target.value;
            e.preventDefault();
            scanQueueRef.current = scanQueueRef.current
                .then(async () => {
                    if (!formikRef.current) return;
                    const currentValues = formikRef.current.values;
                    const currentSetFieldValue = formikRef.current.setFieldValue;
                    await processScannedBarcode(val, currentValues, currentSetFieldValue);
                })
                .catch((err) => {
                    console.error("[ReturCustomer Create] Manual scan queue error:", err);
                });
        }
    };

    const deleteItem = (indexProductVariant, indexProductCode, { setFieldValue, values }) => {
        const newProductStockOpname = [...values?.items];
        const productVariant = newProductStockOpname[indexProductVariant];

        // Remove the specific product code
        productVariant.product_code.splice(indexProductCode, 1);
        productVariant.qty -= 1;

        // If there are no more product codes left, remove the product variant
        if (productVariant.product_code.length === 0) {
            newProductStockOpname.splice(indexProductVariant, 1);
        }

        setFieldValue('items', newProductStockOpname);
    };

    const handleSuccessSalesOrder = (data) => {
        const updatedOrderItems = data?.OrderItems?.map(item => {
            const product_variant = productVariants?.find((variant) =>
                variant?.product_id === item?.product_id &&
                variant?.color_variant_id === item?.color_variant_id &&
                variant?.size_variant_id === item?.size_variant_id
            );

            return {
                ...item, // Spread the existing properties
                product_variant, // Add the new property
            };
        });

        // Create a new data object with updated OrderItems
        const updatedData = {
            ...data,
            OrderItems: updatedOrderItems,
        };
        setOrder(updatedData);
        orderRef.current = updatedData;
        if (formikRef.current) {
            formikRef.current.setFieldValue("items", []);
        }
        closeModal("salesOrder.view");
    };

    const addItemRetur = async (item, setFieldValue, values) => {
        const isExist = values?.items?.find((element) => {
            return element?.product_variant_id == item?.product_variant?.id
        });

        if (!isExist) {
            // Produk per qty: tidak ada `product_codes` untuk dicari, jadi
            // barisnya dibuat langsung dengan jumlah. Batas atasnya jumlah di
            // order dikurangi yang sudah pernah diretur — jalur barcode unik
            // mendapat batas yang sama secara tidak langsung lewat unit yang
            // masih berstatus `delivery`.
            if (isPerQtyOrderItem(item)) {
                if (gagalRiwayatRetur) {
                    setPayloads("errorMessage.view", {
                        title: "Riwayat retur belum terbaca",
                        message: "Jumlah yang sudah diretur pada order ini gagal dimuat, sehingga batas retur tidak diketahui. Muat ulang halaman atau pilih ulang order sebelum menambahkan produk per qty.",
                    });
                    openModal("errorMessage.view");
                    return;
                }
                const sisa = sisaReturPerQty(item);
                if (sisa <= 0) {
                    setPayloads("errorMessage.view", {
                        title: "Semua produk sudah diretur.",
                        message: `Seluruh ${item?.qty} ${item?.master_product?.name ?? "produk"} pada order ini sudah diretur.`,
                    });
                    openModal("errorMessage.view");
                    return;
                }
                const newItem = {
                    product_variant_id: item?.product_variant?.id,
                    product_variant: item?.product_variant,
                    product_code: [],
                    qty: 1,
                    max: sisa,
                    is_unique_barcode: false,
                };
                setFieldValue('items', [...values.items, newItem]);
                return;
            }

            try {
                // 404 = tidak ada unit tersisa, bukan galat — lihat `cariDaftar`.
                const productCodeAPI = await cariDaftar(() => api.productCode.index({
                    product_variant_id: item?.product_variant?.id,
                    sales_order_id: order?.sales_order_id,
                }))

                let productCodeList = productCodeAPI?.data?.filter((code) => code?.status === "delivery");

                if (productCodeList?.length == 0) {
                    setPayloads("errorMessage.view", {
                        title: "Error.",
                        message: "Semua produk sudah diretur.",
                    });
                    openModal("errorMessage.view");
                    return;
                }
                const productCode = productCodeList?.length > 0 ? productCodeList[0] : null;
                const newItem = {
                    product_variant_id: item?.product_variant?.id,
                    product_variant: item?.product_variant,
                    product_code: [productCode],
                    qty: 1,
                    max: item?.qty
                };
                setFieldValue('items', [...values.items, newItem]);
                setExpandedRows((prev) => ({ ...prev, [values.items.length]: true }));
            } catch (error) {
                console.error("Error fetching product code:", error);
                setPayloads("errorMessage.view", {
                    title: "API Error",
                    message: error?.response?.data?.message || "Error fetching product code",
                });
                isError = true;
                openModal("errorMessage.view");
                return;
            }
        }
    };

    /** Mengubah jumlah baris retur per qty, dibatasi sisa yang boleh diretur. */
    const setQtyPerQty = (item, nilai, setFieldValue, values) => {
        const qty = clampReturQty(nilai, item?.max);
        if (Number(nilai) > Number(item?.max)) {
            showToast(`Maksimal ${item?.max} untuk produk ini.`, "error");
        }
        setFieldValue('items', values.items.map((element) =>
            element === item ? { ...element, qty } : element));
    };

    /** Menghapus seluruh baris retur — dipakai baris per qty yang tak punya unit. */
    const removeItem = (key, { setFieldValue, values }) => {
        setFieldValue('items', values.items.filter((_, index) => index !== key));
    };

    const addProductCode = async (item, setFieldValue, values) => {
        if (item?.max <= item?.qty) {
            // Item scanned is not within shopping list
            setPayloads("errorMessage.view", {
                title: "Maksimal jumlah pesanan.",
                message: `Produk yang dipesan hanya ${item?.max}.`,
            });
            // isError = true;
            openModal("errorMessage.view");
        } else {
            try {
                // 404 = tidak ada unit tersisa, bukan galat — lihat `cariDaftar`.
                const productCodeAPI = await cariDaftar(() => api.productCode.index({
                    product_variant_id: item?.product_variant?.id,
                    sales_order_id: order?.sales_order_id,
                }))

                const existingProductCode = values?.items?.map((element) => {
                    return element?.product_code?.map((code) => code?.qr_code)
                });

                let productCodeList = productCodeAPI?.data?.filter((code) => code?.status === "delivery");

                if (productCodeList?.length == 0) {
                    setPayloads("errorMessage.view", {
                        title: "Semua produk sudah diretur.",
                        message: "Semua produk sudah diretur.",
                    });
                    openModal("errorMessage.view");
                    return;
                }

                const productCode = productCodeList ? productCodeList.find(code => !existingProductCode[0].includes(code?.qr_code)) : null;

                if (!productCode && item?.max > item?.qty) {
                    setPayloads("errorMessage.view", {
                        title: "Produk tidak ditemukan.",
                        message: `Ada ${item?.max - item?.qty} produk yang sudah diretur.`,
                    });
                    openModal("errorMessage.view");
                    return;
                }

                if (!productCode) {
                    setPayloads("errorMessage.view", {
                        title: "Semua produk sudah diretur.",
                        message: "Semua produk sudah diretur.",
                    });
                    openModal("errorMessage.view");
                    return;
                }

                const newItem = values?.items?.map((element) => {
                    if (element.product_variant_id === item?.product_variant?.id) {
                        element.product_code = [...element.product_code, productCode];
                        element.qty += 1;
                    }
                    return element;
                });
                setFieldValue('items', newItem);
            } catch (error) {
                console.error("Error fetching product code:", error);
                setPayloads("errorMessage.view", {
                    title: "API Error",
                    message: error?.response?.data?.message || "Error fetching product code",
                });
                isError = true;
                openModal("errorMessage.view");
                return;
            }
        }
    };

    const handleSuccessProductChoose = (product) => {
        const updatedOrderItems = product?.sales_order?.OrderItems?.map(item => {
            const product_variant = productVariants?.find((variant) =>
                variant?.product_id === item?.product_id &&
                variant?.color_id === item?.color_id &&
                variant?.size_id === item?.size_id
            );

            return {
                ...item, // Spread the existing properties
                product_variant, // Add the new property
            };
        });

        // Create a new data object with updated OrderItems
        const updatedData = {
            MCustomer: product?.sales_order?.MCustomer,
            SalesOrder: product?.sales_order,
            OrderItems: updatedOrderItems,
        };
        console.log("updatedData", product);

        const salesOrderId = order?.SalesOrder?.id;
        setOrder(updatedData);
        orderRef.current = updatedData;

        if (formikRef.current) {
            const values = formikRef.current?.values;
            const newVariant = {
                product_variant_id: product?.product_variant?.id,
                product_variant: product?.product_variant,
                product_code: [product],
                qty: 1
            };

            if (salesOrderId === order?.SalesOrder?.id) {
                const existingItem = values?.items?.find(item => item.product_variant_id === product?.product_variant_id);
                if (existingItem) {
                    const isQrCodeExists = existingItem.product_code?.find(item => item.qr_code === product?.qr_code);
                    if (!isQrCodeExists) {
                        existingItem.product_code = [...existingItem.product_code, product];
                        existingItem.qty += 1;
                        formikRef.current.setFieldValue('items', [existingItem]);
                    }
                    console.log("existingItem", existingItem);
                } else {
                    formikRef.current.setFieldValue('items', [...values?.items, newVariant]);
                    console.log("newVariant", newVariant);
                }
            } else {
                formikRef.current.setFieldValue('items', [newVariant]);
            }

            setExpandedRows((prev) => ({ ...prev, [values.items.length]: true }));

            showToast("Anda telah menambahkan produk ke dalam retur", "success");
        }
    };

    return (
        <MainLayout
            activeSidebarNavigation="warehouse"
            activeSidebarChild="retur-customer"
            breadcrumbItems={breadcrumbItems}
            pageTitle="Tambah Retur Customer"
            headingButtons={[
                {
                    label: "Pilih Order",
                    type: "button",
                    onClick: () => {
                        setPayloads("salesOrder.choose", order);
                        openModal("salesOrder.choose");
                    },
                },
                {
                    label: "Scan Produk",
                    type: "button",
                    onClick: () => {
                        setPayloads("product.scanQrProductCodeReturCustomer", {status: "delivery"});
                        openModal("product.scanQrProductCodeReturCustomer");
                    },
                },
                {
                    label: "Riwayat Retur",
                    type: "button",
                    onClick: () => {
                        if (order?.id) {
                            console.log(order?.id);
                            setPayloads("returHistory.view", {transactionId: order?.id});
                            openModal("returHistory.view");
                        } else {
                            showToast("Anda belum memilih order", "error");
                        }
                    },
                },
            ]}
        >
            <AppModals
                items={["errorMessage.view", "salesOrder.choose", "product.scanQrProductCodeReturCustomer", "returHistory.view"]}
                onSuccess={{
                    "errorMessage.view": () => fetch(),
                    "salesOrder.choose": (order) => handleSuccessSalesOrder(order),
                    "product.scanQrProductCodeReturCustomer": (product) => handleSuccessProductChoose(product),
                    "returHistory.view": () => fetch(),
                }}
            />
            <Formik
                {...{ initialValues, validationSchema, onSubmit }}
                enableReinitialize={true}
                innerRef={formikRef}
            >
                {({
                    values,
                    errors,
                    status,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                    setFieldValue
                }) => (
                    <form
                        className={clsx(
                            "px-2 overflow-y-auto",
                            style["main-content-height"]
                        )}
                        onSubmit={handleSubmit}
                    >
                        <FormHeader>
                            <InputMolecule testId="retur-customer-create-order-code-input"
                                label="Order Code"
                                type="text"
                                name={`order_code`}
                                onChange={(e) => {
                                    handleChange(e);
                                    setFieldValue("items", [])
                                }}
                                onBlur={handleBlur}
                                value={order?.SalesOrder?.order_code}
                                disabled
                            />
                            <InputMolecule testId="retur-customer-create-customer-name-input"
                                label="Customer"
                                type="text"
                                name={`customer_name`}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={order?.MCustomer?.nickName}
                                disabled
                            />
                            <div className="">
                                <InputLabel>Scan Barcode Disini</InputLabel>
                                <input data-testid="retur-customer-create-scan-barcode-input"
                                    data-allow-global-scan="true"
                                    className="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-gray-900 placeholder-gray-300 shadow-sm focus:border-primary-600 focus:outline-none disabled:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500 autofill:dark:bg-gray-800 dark:disabled:bg-gray-800 focus:ring-1 focus:ring-primary-600"
                                    type="text"
                                    name="scan_barcode"
                                    onChange={(e) => {
                                        setQrCode(e.target.value);
                                        handleScanBarcodeChange(e, { setFieldValue, values });
                                    }}
                                    onKeyDown={(e) => {
                                        // Trigger scanning on Enter key
                                        if (e.key === "Enter") {
                                            e.preventDefault(); // Prevent the default form submission
                                            handleScanBarcodeChange(e, { setFieldValue, values });
                                        }
                                    }}
                                    onBlur={(e) => {
                                        handleBlur(e);
                                        // Immediately refocus if blur occurs
                                        if (scanBarcodeInputRef.current) {
                                            scanBarcodeInputRef.current.focus();
                                            scanBarcodeInputRef.current.select();
                                        }
                                    }}
                                    value={qrCode}
                                />
                            </div>
                        </FormHeader>

                        <Table.Wrapper asIndex={true}>
                            <Table.Header>
                                <Table.HeaderRow>
                                    <Table.Heading>Order Item</Table.Heading>
                                    <Table.Heading>Quantity</Table.Heading>
                                    <Table.Heading>Action</Table.Heading>
                                </Table.HeaderRow>
                            </Table.Header>
                            <Table.Body>
                                {order?.OrderItems?.map((item, index) => (
                                    <Table.BodyRow key={index}>
                                        <Table.Cell>{item.master_product?.name} {item.master_color?.name} {item.master_size?.name}</Table.Cell>
                                        <Table.Cell>
                                            {item.qty}
                                            {isPerQtyOrderItem(item) && (
                                                <span className="ml-2 text-xs text-gray-500">
                                                    per qty · {gagalRiwayatRetur ? "sisa belum terbaca" : `sisa ${sisaReturPerQty(item)}`}
                                                </span>
                                            )}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <IconButton testId="retur-customer-create-add-order-item-button"
                                                icon={PlusCircleIcon}
                                                onClick={() => addItemRetur(item, setFieldValue, values)}
                                            />
                                        </Table.Cell>
                                    </Table.BodyRow>
                                ))}
                            </Table.Body>
                        </Table.Wrapper>

                        <h1 className="text-lg font-semibold mb-4">Produk diretur</h1>
                        <Table.Container>
                            <Table.Wrapper asIndex={false}>
                                <Table.Header>
                                    <Table.HeaderRow>
                                        <Table.Heading>Product Name</Table.Heading>
                                        <Table.Heading>Warna</Table.Heading>
                                        <Table.Heading>Ukuran</Table.Heading>
                                        <Table.Heading>Jumlah</Table.Heading>
                                        <Table.Heading>Actions</Table.Heading>
                                    </Table.HeaderRow>
                                </Table.Header>
                                <Table.Body>
                                    {values?.items?.map((returCustomer, key) => {
                                        return (
                                            <React.Fragment key={key}>
                                                <Table.BodyRow key={key}>
                                                    <Table.Cell>
                                                        <strong>{returCustomer?.product_variant?.product_detail?.name}</strong>
                                                    </Table.Cell>
                                                    <Table.Cell>{returCustomer?.product_variant?.color?.name}</Table.Cell>
                                                    <Table.Cell>{returCustomer?.product_variant?.size?.name}</Table.Cell>
                                                    <Table.Cell>
                                                        {returCustomer?.is_unique_barcode === false ? (
                                                            <input
                                                                data-testid={`retur-customer-create-qty-input-${returCustomer?.product_variant_id}`}
                                                                type="number"
                                                                min={1}
                                                                max={returCustomer?.max}
                                                                className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                                value={returCustomer?.qty}
                                                                onChange={(e) => setQtyPerQty(returCustomer, e.target.value, setFieldValue, values)}
                                                            />
                                                        ) : (
                                                            returCustomer?.qty
                                                        )}
                                                    </Table.Cell>
                                                    <Table.Cell>
                                                        <div className="flex items-center justify-center gap-2">
                                                            {returCustomer?.is_unique_barcode === false ? (
                                                                /* Baris per qty tidak punya unit untuk ditambah atau
                                                                   dibuka satu per satu — jumlahnya diketik langsung,
                                                                   dan penghapusan membuang seluruh barisnya. */
                                                                <IconButton testId="retur-customer-create-remove-item-button"
                                                                    icon={TrashIcon}
                                                                    variant="danger"
                                                                    onClick={() => removeItem(key, { setFieldValue, values })}
                                                                />
                                                            ) : (
                                                                <>
                                                                    <IconButton testId="retur-customer-create-add-unit-button"
                                                                        icon={PlusCircleIcon}
                                                                        onClick={() => addProductCode(returCustomer, setFieldValue, values)}
                                                                    />
                                                                    <IconButton testId="retur-customer-create-chevron-up-button"
                                                                        icon={expandedRows[key] ? ChevronUpIcon : ChevronDownIcon}
                                                                        onClick={() => toggleRow(key)}
                                                                    />
                                                                </>
                                                            )}
                                                        </div>
                                                    </Table.Cell>
                                                </Table.BodyRow>
                                                {expandedRows[key] && returCustomer?.product_code?.map((productCode, index) => (
                                                    <Table.BodyRow key={index}>
                                                        <Table.Cell></Table.Cell>
                                                        <Table.Cell>{productCode?.qr_code}</Table.Cell>
                                                        <Table.Cell colSpan={2}>
                                                            <textarea data-testid="retur-customer-create-tambah-catatan-optional-textarea"
                                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                                rows={2}
                                                                maxLength={255}
                                                                placeholder="Tambah Catatan (optional)"
                                                                value={productCode?.notes || ''}
                                                                onChange={(e) =>
                                                                    setFieldValue(
                                                                        `items.${key}.product_code.${index}.notes`,
                                                                        e.target.value
                                                                    )
                                                                }
                                                            />
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            <div className="flex items-center justify-center gap-2">
                                                                <IconButton testId="retur-customer-create-trash-button"
                                                                    onClick={() => deleteItem(key, index, { setFieldValue, values })}
                                                                    icon={TrashIcon}
                                                                    variant="danger"
                                                                />
                                                            </div>
                                                        </Table.Cell>
                                                    </Table.BodyRow>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                </Table.Body>
                            </Table.Wrapper>
                        </Table.Container>
                        <ErrorMessage>{getIn(errors, "items")}</ErrorMessage>

                        <FormSummary className="w-full">
                            <div className="w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Catatan
                                </label>
                                <textarea data-testid="retur-customer-create-notes-textarea"
                                    name="notes"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values?.notes || ''}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    rows="3"
                                    placeholder="Tulis catatan disini..."
                                />
                            </div>
                        </FormSummary>

                        <div className="fixed bottom-2 left-0 sm:left-auto right-0 sm:right-4 sm:bottom-4 sm:w-auto z-40 flex justify-end border-t border-gray-100 bg-white sm:bg-transparent p-4 sm:p-0 dark:border-gray-700 dark:bg-gray-800 print:hidden sm:absolute sm:rounded-b-lg sm:border-0 ">
                            <Button testId="retur-customer-create-submit-button" type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Menyimpan...' : 'Simpan'}
                            </Button>
                        </div>
                    </form>
                )}
            </Formik>
        </MainLayout>
    );
}

export default CreateReturCustomer;
